"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
	createFilters,
	type FetchProductsOptions,
} from "@trydig/centra/filters";
import { withSearchParams } from "@trydig/ui/next";
import { searchParamsToObject } from "@trydig/utils";
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
	useRef,
} from "react";
import { rpc } from "#rpc";
import { PAGE_SIZE } from "@/constants";
import type { ProductListData } from "@/data/routes/products.route";

export type StoryLinkOrCentraCategory =
	| {
			text: string;
			href?: string;
			root?: boolean;
	  }
	| {
			text: string;
			centraCategory: string;
			root?: boolean;
	  };

interface ProductListingContext {
	query: ReturnType<typeof useProductListingQuery>;
	filterKeys: string[];
	filter: ProductListData["filter"];
	firstPage: ProductListData | undefined;
	lastPage: ProductListData | undefined;
	total: number;
	filterLinks?: StoryLinkOrCentraCategory[];
	slug: string;
}

const ProductListingContext = createContext<ProductListingContext>(null!);

interface ProductListingProviderProps {
	initialData?: ProductListData;
	options: FetchProductsOptions;
	categoryId: string | undefined;
	filterKeys: string[];
	filterLinks?: StoryLinkOrCentraCategory[];
	slug: string;
}

export const ProductListingProvider = withSearchParams<
	PropsWithChildren<ProductListingProviderProps>
>(
	({
		children,
		initialData,
		options,
		searchParams,
		categoryId,
		filterKeys,
		filterLinks,
		slug,
	}) => {
		const query = useProductListingQuery({
			initialData,
			options,
			searchParams,
			categoryId,
			filterKeys,
		});

		const pages = query.data?.pages || [];

		const total = pages.length > 0 ? (pages[pages.length - 1]?.total ?? 0) : 0;

		const firstPage = pages.length > 0 ? pages[0] : undefined;
		const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;
		const filter = lastPage?.filter || [];
		return (
			<ProductListingContext.Provider
				value={{
					query,
					filterKeys,
					filter,
					firstPage,
					lastPage,
					total,
					filterLinks,
					slug,
				}}
			>
				{children}
			</ProductListingContext.Provider>
		);
	},
);

export const useProductListingQuery = ({
	initialData,
	options,
	searchParams,
	categoryId,
	filterKeys,
}: {
	initialData?: ProductListData;
	options: FetchProductsOptions;
	searchParams: URLSearchParams;
	categoryId: string | undefined;
	filterKeys: string[];
}) => {
	const trackedInitialData = useRef<ProductListData | undefined>(
		initialData as ProductListData,
	);

	const [clientOptions, cache] = useMemo(() => {
		const [filters, { cache }] = createFilters(
			searchParamsToObject(searchParams),
			{
				filterKeys: filterKeys,
				searchKey: "q",
				categoryId: categoryId,
			},
		);
		return [
			{
				...options,
				...filters,
			},
			cache,
		] as const;
	}, [options, filterKeys, searchParams, categoryId]);

	return useInfiniteQuery({
		queryKey: ["fetchProducts", clientOptions],
		queryFn: ({ pageParam }) => {
			return rpc.products.list(
				{
					...clientOptions,
					skipFirst: pageParam * PAGE_SIZE,
				},
				cache,
			);
		},
		initialData: trackedInitialData.current
			? {
					pages: [trackedInitialData.current],
					pageParams: [0],
				}
			: undefined,

		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) =>
			lastPage.total! > PAGE_SIZE * allPages.length
				? allPages.length
				: undefined,
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});
};

export const useProducts = () => {
	const ctx = useContext(ProductListingContext);

	if (!ctx) {
		throw new Error("useProducts must be used within a ProductListingProvider");
	}

	return ctx;
};
